"use client";

import { isServer } from "@decibeltrade/web-core/utils";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

import { MAX_SLIPPAGE_ALLOWED, UserPreferenceKeys } from "@/constants";
import {
  type DelegationsRecord,
  type UpdateUserPreferencesRequestBody,
  useUpdateUserPreferences,
} from "@/contexts/mutations/update-user-preferences";
import {
  userPreferencesQuery,
  type UserPreferencesWithSubaccountsResponseData,
} from "@/contexts/queries/user-preferences";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { getLocalStorageItem, setLocalStorageItem } from "@/hooks/storage";
import { useAuthState } from "@/hooks/use-auth-state";
import { addressesEqual } from "@/utils/address";
import { Logger } from "@/utils/logger";
import { GetAuthHeaders } from "@/utils/signature-auth";

export enum TradingModes {
  BASIC = "basic",
  ADVANCED = "advanced",
}

const DEFAULT_PREFERENCES = {
  lastLocalUpdate: "",
  maxSlippage: 8,
  marketCloseConfirmation: true,
  placeOrderConfirmation: true,
  tradingMode: TradingModes.BASIC,
  customSubaccountNames: {} as Record<string, string>,
  watchedMarkets: [] as string[],
  delegations: {} as DelegationsRecord,
};

type UpdatablePreferenceKey =
  | "marketCloseConfirmation"
  | "placeOrderConfirmation"
  | "maxSlippage"
  | "tradingMode"
  | "watchlist"
  | "customSubaccountNames"
  | "delegations";

interface UserPreferencesStore {
  accountAddress?: string;
  subaccountAddress?: string;
  error?: string;
  hasHydratedFromLocalStorage: boolean;
  lastLocalUpdate: string;
  maxSlippage: number;
  marketCloseConfirmation: boolean;
  placeOrderConfirmation: boolean;
  tradingMode: TradingModes;
  customSubaccountNames: Record<string, string>;
  watchedMarkets: string[];
  delegationsMetadata: DelegationsRecord;
  initializeLocalState: () => void;
  setAccount: (accountAddress?: string, subaccountAddress?: string) => void;
  setLastLocalUpdate: (value: string) => void;
  applyRemotePreferences: (args: {
    data: UserPreferencesWithSubaccountsResponseData;
    subaccountAddress?: string;
  }) => void;
  applyLocalPreference: <K extends UpdatablePreferenceKey>(
    key: K,
    value: NonNullable<UpdateUserPreferencesRequestBody[K]>,
  ) => void;
  clearLocalUserPreferences: () => void;
  getPreferencesPayload: () => UpdateUserPreferencesRequestBody;
}

type PreferenceStoreField =
  | "marketCloseConfirmation"
  | "placeOrderConfirmation"
  | "maxSlippage"
  | "tradingMode"
  | "watchedMarkets"
  | "customSubaccountNames"
  | "delegationsMetadata";

type PreferenceStateSlice = Pick<UserPreferencesStore, PreferenceStoreField>;
type MutablePreferenceState = Record<
  PreferenceStoreField,
  PreferenceStateSlice[PreferenceStoreField]
>;

type RemoteSelection<T> = { hasValue: false } | { hasValue: true; value?: T };

interface PreferenceDescriptor {
  storageKey: (typeof UserPreferenceKeys)[keyof typeof UserPreferenceKeys];
  storeField: PreferenceStoreField;
  defaultValue: UserPreferencesStore[PreferenceStoreField];
  selectRemoteValue: (
    remote: UserPreferencesWithSubaccountsResponseData,
    subaccountAddress?: string,
  ) => RemoteSelection<unknown>;
}

interface SyncArgs {
  accountAddress: string;
  subaccountAddress?: string;
  mutate: ReturnType<typeof useUpdateUserPreferences>["mutateAsync"];
}

const readLocalStorageValue = <T>(key: string, fallback: T): T => {
  if (isServer()) return fallback;
  try {
    const storedValue = getLocalStorageItem(key);
    if (storedValue === null) {
      return fallback;
    }
    return JSON.parse(storedValue) as T;
  } catch (error) {
    Logger.error(error, {
      message: "Failed to parse local storage value",
      key,
    });
    return fallback;
  }
};

const hasOwnPreference = <T extends object>(
  obj: T,
  key: keyof T,
): obj is T & Record<typeof key, unknown> => Object.prototype.hasOwnProperty.call(obj, key);

const preferenceDescriptors: Record<UpdatablePreferenceKey, PreferenceDescriptor> = {
  marketCloseConfirmation: {
    storageKey: UserPreferenceKeys.MARKET_CLOSE_CONFIRMATION,
    storeField: "marketCloseConfirmation",
    defaultValue: DEFAULT_PREFERENCES.marketCloseConfirmation,
    selectRemoteValue: ({ userPreferences }) => ({
      hasValue: hasOwnPreference(userPreferences, "marketCloseConfirmation"),
      value: userPreferences.marketCloseConfirmation,
    }),
  },
  placeOrderConfirmation: {
    storageKey: UserPreferenceKeys.PLACE_ORDER_CONFIRMATION,
    storeField: "placeOrderConfirmation",
    defaultValue: DEFAULT_PREFERENCES.placeOrderConfirmation,
    selectRemoteValue: ({ userPreferences }) => ({
      hasValue: hasOwnPreference(userPreferences, "placeOrderConfirmation"),
      value: userPreferences.placeOrderConfirmation,
    }),
  },
  maxSlippage: {
    storageKey: UserPreferenceKeys.MAX_SLIPPAGE,
    storeField: "maxSlippage",
    defaultValue: DEFAULT_PREFERENCES.maxSlippage,
    selectRemoteValue: ({ userPreferences }) => ({
      hasValue: hasOwnPreference(userPreferences, "maxSlippage"),
      value: userPreferences.maxSlippage,
    }),
  },
  tradingMode: {
    storageKey: UserPreferenceKeys.TRADING_MODE,
    storeField: "tradingMode",
    defaultValue: DEFAULT_PREFERENCES.tradingMode,
    selectRemoteValue: ({ userPreferences }) => ({
      hasValue: hasOwnPreference(userPreferences, "tradingMode"),
      value: userPreferences.tradingMode,
    }),
  },
  customSubaccountNames: {
    storageKey: UserPreferenceKeys.CUSTOM_SUBACCOUNT_NAMES,
    storeField: "customSubaccountNames",
    defaultValue: DEFAULT_PREFERENCES.customSubaccountNames,
    selectRemoteValue: ({ userPreferences }) => ({
      hasValue: hasOwnPreference(userPreferences, "customSubaccountNames"),
      value: userPreferences.customSubaccountNames,
    }),
  },
  watchlist: {
    storageKey: UserPreferenceKeys.WATCHED_MARKETS,
    storeField: "watchedMarkets",
    defaultValue: DEFAULT_PREFERENCES.watchedMarkets,
    selectRemoteValue: ({ userPreferences }) => ({
      hasValue: hasOwnPreference(userPreferences, "watchlist"),
      value: userPreferences.watchlist,
    }),
  },
  delegations: {
    storageKey: UserPreferenceKeys.CUSTOM_DELEGATIONS_METADATA,
    storeField: "delegationsMetadata",
    defaultValue: DEFAULT_PREFERENCES.delegations,
    selectRemoteValue: ({ subaccounts }, subaccountAddress) => {
      if (!Array.isArray(subaccounts) || !subaccountAddress) {
        return { hasValue: false };
      }
      const delegations = subaccounts.find((subaccount) =>
        addressesEqual(subaccount.subaccountAddress, subaccountAddress),
      )?.delegations;
      return {
        hasValue: typeof delegations !== "undefined",
        value: delegations,
      };
    },
  },
} as const;

const getPreferenceDescriptor = (key: UpdatablePreferenceKey) => preferenceDescriptors[key];

const preferenceKeys = Object.keys(preferenceDescriptors) as UpdatablePreferenceKey[];

const resolvePreferenceValue = ({
  key,
  state,
  value,
  source,
}: {
  key: UpdatablePreferenceKey;
  state: UserPreferencesStore;
  value: unknown;
  source: "remote" | "local";
}) => {
  if (key === "delegations" && source === "local") {
    return {
      ...state.delegationsMetadata,
      ...(value as DelegationsRecord),
    };
  }
  return value as PreferenceStateSlice[PreferenceStoreField];
};

const buildPreferencePatch = ({
  state,
  key,
  value,
  source,
}: {
  state: UserPreferencesStore;
  key: UpdatablePreferenceKey;
  value: unknown;
  source: "remote" | "local";
}): Partial<UserPreferencesStore> => {
  const descriptor = getPreferenceDescriptor(key);
  const resolvedValue = resolvePreferenceValue({ key, state, value, source });
  setLocalStorageItem(descriptor.storageKey, resolvedValue);
  return {
    [descriptor.storeField]: resolvedValue,
  };
};

const assignPreferenceStateValue = (
  target: Partial<PreferenceStateSlice>,
  descriptor: PreferenceDescriptor,
  value: PreferenceStateSlice[PreferenceStoreField],
) => {
  const mutableTarget = target as MutablePreferenceState;
  mutableTarget[descriptor.storeField] = value;
};

export const useUserPreferencesStore = create<UserPreferencesStore>()((set, get) => ({
  accountAddress: undefined,
  subaccountAddress: undefined,
  error: undefined,
  hasHydratedFromLocalStorage: false,
  lastLocalUpdate: DEFAULT_PREFERENCES.lastLocalUpdate,
  maxSlippage: DEFAULT_PREFERENCES.maxSlippage,
  marketCloseConfirmation: DEFAULT_PREFERENCES.marketCloseConfirmation,
  placeOrderConfirmation: DEFAULT_PREFERENCES.placeOrderConfirmation,
  tradingMode: DEFAULT_PREFERENCES.tradingMode,
  customSubaccountNames: DEFAULT_PREFERENCES.customSubaccountNames,
  watchedMarkets: DEFAULT_PREFERENCES.watchedMarkets,
  delegationsMetadata: DEFAULT_PREFERENCES.delegations,
  initializeLocalState: () => {
    if (get().hasHydratedFromLocalStorage) return;
    if (isServer()) {
      set({ hasHydratedFromLocalStorage: true });
      return;
    }

    const preferenceState: Partial<PreferenceStateSlice> = {};
    for (const key of preferenceKeys) {
      const descriptor = getPreferenceDescriptor(key);
      const value = readLocalStorageValue(descriptor.storageKey, descriptor.defaultValue);
      assignPreferenceStateValue(preferenceState, descriptor, value);
    }

    set({
      hasHydratedFromLocalStorage: true,
      lastLocalUpdate: readLocalStorageValue(
        UserPreferenceKeys.LAST_LOCAL_UPDATE,
        DEFAULT_PREFERENCES.lastLocalUpdate,
      ),
      ...preferenceState,
    });
  },
  setAccount: (accountAddress, subaccountAddress) =>
    set(() => ({
      accountAddress,
      subaccountAddress,
    })),
  setLastLocalUpdate: (value) => {
    setLocalStorageItem(UserPreferenceKeys.LAST_LOCAL_UPDATE, value);
    set({ lastLocalUpdate: value });
  },
  applyRemotePreferences: ({ data, subaccountAddress }) => {
    set((state) => {
      let nextState = state;
      for (const key of preferenceKeys) {
        const descriptor = getPreferenceDescriptor(key);
        const selection = descriptor.selectRemoteValue(data, subaccountAddress);
        if (!selection.hasValue) continue;
        const incomingValue = (selection.value ?? descriptor.defaultValue) as NonNullable<
          UpdateUserPreferencesRequestBody[typeof key]
        >;
        const patch = buildPreferencePatch({
          state: nextState,
          key,
          value: incomingValue,
          source: "remote",
        });
        nextState = { ...nextState, ...patch };
      }
      return nextState;
    });
  },
  applyLocalPreference: (key, value) => {
    set((state) => ({
      ...state,
      ...buildPreferencePatch({
        state,
        key,
        value,
        source: "local",
      }),
    }));
  },
  clearLocalUserPreferences: () => {
    setLocalStorageItem(UserPreferenceKeys.LAST_LOCAL_UPDATE, DEFAULT_PREFERENCES.lastLocalUpdate);
    const resetState: Partial<PreferenceStateSlice> = {};
    for (const key of preferenceKeys) {
      const descriptor = getPreferenceDescriptor(key);
      setLocalStorageItem(descriptor.storageKey, descriptor.defaultValue);
      assignPreferenceStateValue(resetState, descriptor, descriptor.defaultValue);
    }

    set({
      lastLocalUpdate: DEFAULT_PREFERENCES.lastLocalUpdate,
      ...resetState,
      error: undefined,
    });
  },
  getPreferencesPayload: () => {
    const state = get();
    const payload: UpdateUserPreferencesRequestBody = {};
    for (const key of preferenceKeys) {
      const descriptor = getPreferenceDescriptor(key);
      (payload as Record<string, unknown>)[key] = state[descriptor.storeField];
    }
    return payload;
  },
}));

const syncDbFromState = async ({ accountAddress, subaccountAddress, mutate }: SyncArgs) => {
  const preferences = useUserPreferencesStore.getState().getPreferencesPayload();
  try {
    await mutate({ accountAddress, preferences, subaccountAddress });
    useUserPreferencesStore.getState().setLastLocalUpdate(new Date().toISOString());
  } catch (error) {
    Logger.error(error, {
      message: "Failed to sync local user preferences to DB",
      accountAddress,
      subaccountAddress,
      preferences,
    });
  }
};

const useHydratePreferences = () => {
  const initializeLocalState = useUserPreferencesStore((state) => state.initializeLocalState);

  useEffect(() => {
    initializeLocalState();
  }, [initializeLocalState]);
};

const useWireAccount = (accountAddress?: string, subaccountAddress?: string) => {
  const setAccount = useUserPreferencesStore((state) => state.setAccount);

  useEffect(() => {
    setAccount(accountAddress, subaccountAddress);
  }, [accountAddress, setAccount, subaccountAddress]);
};

interface RemotePreferenceSyncArgs {
  accountAddress?: string;
  subaccountAddress?: string;
  remotePreferences?: UserPreferencesWithSubaccountsResponseData | null;
  shouldEnableQuery: boolean;
  mutate: SyncArgs["mutate"];
}

const useRemotePreferenceSync = ({
  accountAddress,
  subaccountAddress,
  remotePreferences,
  shouldEnableQuery,
  mutate,
}: RemotePreferenceSyncArgs) => {
  const applyRemotePreferences = useUserPreferencesStore((state) => state.applyRemotePreferences);
  const setLastLocalUpdate = useUserPreferencesStore((state) => state.setLastLocalUpdate);

  useEffect(() => {
    if (!accountAddress || !remotePreferences?.userPreferences || !shouldEnableQuery) return;

    const { lastLocalUpdate: currentLastLocalUpdate } = useUserPreferencesStore.getState();

    const dbLastUpdated = new Date(remotePreferences.userPreferences.lastUpdated);
    const localDate = currentLastLocalUpdate ? new Date(currentLastLocalUpdate) : null;
    if (localDate) {
      localDate.setMinutes(localDate.getMinutes() + 1);
    }

    if (!localDate || dbLastUpdated.getTime() > localDate.getTime()) {
      applyRemotePreferences({ data: remotePreferences, subaccountAddress });
      setLastLocalUpdate(remotePreferences.userPreferences.lastUpdated);
    } else {
      void syncDbFromState({
        accountAddress,
        subaccountAddress,
        mutate,
      });
    }
  }, [
    accountAddress,
    subaccountAddress,
    shouldEnableQuery,
    remotePreferences,
    applyRemotePreferences,
    setLastLocalUpdate,
    mutate,
  ]);
};

// Exported pure so tests don't need a React tree. Without getAuthHeaders,
// the wrapper can't retry a 401 with SIWA.
export const shouldEnableUserPreferencesQuery = ({
  ownerWalletAddress,
  hasHydratedFromLocalStorage,
  getAuthHeaders,
}: {
  ownerWalletAddress: string | undefined;
  hasHydratedFromLocalStorage: boolean;
  getAuthHeaders: GetAuthHeaders;
}): boolean => Boolean(ownerWalletAddress && hasHydratedFromLocalStorage && getAuthHeaders);

export function UserPreferencesSync() {
  const { ownerWalletAddress, subaccountAddress } = useAuthState();
  const { getAuthHeaders } = useWriteDexSdk();
  const { mutateAsync: updateUserPreferences } = useUpdateUserPreferences({ getAuthHeaders });

  useHydratePreferences();
  useWireAccount(ownerWalletAddress, subaccountAddress);

  const hasHydratedFromLocalStorage = useUserPreferencesStore(
    (state) => state.hasHydratedFromLocalStorage,
  );
  const shouldEnableQuery = shouldEnableUserPreferencesQuery({
    ownerWalletAddress,
    hasHydratedFromLocalStorage,
    getAuthHeaders,
  });

  const { data: remotePreferences } = useQuery({
    ...userPreferencesQuery({
      accountAddress: ownerWalletAddress ?? "",
      getAuthHeaders,
      subaccountAddress,
    }),
    enabled: shouldEnableQuery,
  });

  useRemotePreferenceSync({
    accountAddress: ownerWalletAddress,
    subaccountAddress,
    remotePreferences,
    shouldEnableQuery,
    mutate: updateUserPreferences,
  });

  return null;
}

export const useUserPreferences = () => {
  const { ownerWalletAddress, subaccountAddress } = useAuthState();
  const { getAuthHeaders } = useWriteDexSdk();
  return useUserPreferencesByAccountAddress(ownerWalletAddress, subaccountAddress, getAuthHeaders);
};

export const useUserPreferencesByAccountAddress = (
  accountAddress: string | undefined,
  subaccountAddress: string | undefined,
  getAuthHeaders: GetAuthHeaders,
) => {
  const { mutateAsync: updateUserPreferences } = useUpdateUserPreferences({ getAuthHeaders });

  const {
    marketCloseConfirmation,
    placeOrderConfirmation,
    customSubaccountNames,
    maxSlippage,
    tradingMode,
    watchedMarkets,
    delegationsMetadata,
    applyLocalPreference,
    setLastLocalUpdate,
    clearLocalUserPreferences,
  } = useUserPreferencesStore(
    useShallow((state) => ({
      marketCloseConfirmation: state.marketCloseConfirmation,
      placeOrderConfirmation: state.placeOrderConfirmation,
      customSubaccountNames: state.customSubaccountNames,
      maxSlippage: state.maxSlippage,
      tradingMode: state.tradingMode,
      watchedMarkets: state.watchedMarkets,
      delegationsMetadata: state.delegationsMetadata,
      applyLocalPreference: state.applyLocalPreference,
      setLastLocalUpdate: state.setLastLocalUpdate,
      clearLocalUserPreferences: state.clearLocalUserPreferences,
    })),
  );

  const makeUpdateFn = useCallback(
    <K extends UpdatablePreferenceKey>(key: K) =>
      async (value: NonNullable<UpdateUserPreferencesRequestBody[K]>) => {
        try {
          if (!accountAddress) {
            throw new Error("No account address found");
          }
          await updateUserPreferences({
            accountAddress,
            preferences: { [key]: value },
            subaccountAddress,
          });
        } catch (error) {
          Logger.error(error, {
            message: "Failed to update user preference",
            accountAddress,
            subaccountAddress,
            key,
            value,
          });
        }

        applyLocalPreference(key, value);
        setLastLocalUpdate(new Date().toISOString());
      },
    [
      accountAddress,
      applyLocalPreference,
      setLastLocalUpdate,
      subaccountAddress,
      updateUserPreferences,
    ],
  );

  return {
    marketCloseConfirmation,
    updateMarketCloseConfirmation: makeUpdateFn("marketCloseConfirmation"),
    placeOrderConfirmation,
    updatePlaceOrderConfirmation: makeUpdateFn("placeOrderConfirmation"),
    customSubaccountNames,
    updateCustomSubaccountNames: makeUpdateFn("customSubaccountNames"),
    maxSlippage:
      // TODO: Remove once we fix the security issues around max slippage
      maxSlippage > MAX_SLIPPAGE_ALLOWED ? MAX_SLIPPAGE_ALLOWED : maxSlippage < 0 ? 0 : maxSlippage,
    updateMaxSlippage: makeUpdateFn("maxSlippage"),
    tradingMode,
    updateTradingMode: makeUpdateFn("tradingMode"),
    watchedMarkets,
    updateWatchedMarkets: makeUpdateFn("watchlist"),
    delegationsMetadata,
    updateDelegationsMetadata: makeUpdateFn("delegations"),
    isAdvancedMode: tradingMode === TradingModes.ADVANCED,
    isBasicMode: tradingMode === TradingModes.BASIC,
    clearLocalUserPreferences,
  };
};
